import axios from 'axios';
import * as config from '@/config-APP_TARGET';

const TOKEN_KEY = 'auth_token';
const USER_KEY = 'auth_user';
const GAME_ID_KEY = 'selected_game_id';
const GAME_MODE_ID_KEY = 'selected_game_mode_id';

/**
 * Richiede magic link per login (deprecato, usa requestLoginCode)
 * @param {string} email
 * @param {string|null} challengeToken
 * @returns {Promise}
 */
export function requestLoginLink(email, challengeToken) {
	if (challengeToken === undefined) {
		challengeToken = null;
	}
	var data = {
		email: email,
		frontendUrl: window.location.origin
	};
	if (challengeToken) {
		data.challenge_token = challengeToken;
	}
	return axios.post(config.apiUrl + '/api/auth/request-login-link', data);
}

/**
 * Richiede codice OTP per login
 * @param {string} email
 * @param {string|null} challengeToken
 * @returns {Promise}
 */
export function requestLoginCode(email, challengeToken) {
	if (challengeToken === undefined) {
		challengeToken = null;
	}
	var data = {
		email: email
	};
	if (challengeToken) {
		data.challenge_token = challengeToken;
	}
	return axios.post(config.apiUrl + '/api/auth/request-login-code', data);
}

/**
 * Verifica codice OTP e ottiene token JWT
 * @param {string} code - Codice OTP a 4 cifre
 * @param {string} email
 * @returns {Promise}
 */
export function verifyOtpCode(code, email) {
	return axios.post(config.apiUrl + '/api/auth/verify-otp-code', {
		code: code,
		email: email
	});
}

/**
 * Verifica magic link e ottiene token JWT
 * @param {string} token
 * @param {string} email
 * @returns {Promise}
 */
export function verifyMagicLink(token, email) {
	return axios.get(`${config.apiUrl}/api/auth/verify-magic-link`, {
		params: {
			token: token,
			email: email
		}
	});
}

/**
 * Salva token JWT in localStorage
 * @param {string} token
 */
export function saveToken(token) {
	try {
		console.log('saveToken chiamato, token:', token ? token.substring(0, 20) + '...' : 'null');
		
		if (!token) {
			console.error('saveToken: Token è null o undefined');
			return;
		}
		
		// Verifica che localStorage sia disponibile
		if (typeof(Storage) === "undefined") {
			console.error('saveToken: localStorage non disponibile');
			return;
		}
		
		// Salva il token
		localStorage.setItem(TOKEN_KEY, token);
		
		// Verifica immediatamente che sia stato salvato
		const saved = localStorage.getItem(TOKEN_KEY);
		if (saved === token) {
			console.log('Token salvato correttamente in localStorage');
		} else {
			console.error('saveToken: Token NON salvato correttamente! Salvato:', saved ? saved.substring(0, 20) + '...' : 'null', 'Atteso:', token.substring(0, 20) + '...');
		}
		
		// Aggiorna header Authorization per tutte le richieste future
		axios.defaults.headers.common['Authorization'] = 'Bearer ' + token;
		console.log('Header Authorization impostato correttamente');
	} catch (e) {
		console.error('Errore nel salvataggio del token:', e);
	}
}

/**
 * Recupera token JWT da localStorage
 * @returns {string|null}
 */
export function getToken() {
	const token = localStorage.getItem(TOKEN_KEY);
	console.log('getToken chiamato, token:', token ? token.substring(0, 20) + '...' : 'null');
	return token;
}

/**
 * Rimuove token JWT da localStorage
 */
export function removeToken() {
	localStorage.removeItem(TOKEN_KEY);
	localStorage.removeItem(USER_KEY);
	delete axios.defaults.headers.common['Authorization'];
}

/**
 * Salva dati utente in localStorage
 * @param {Object} user - Oggetto utente con id, email, nickname, ecc.
 */
export function saveUser(user) {
	try {
		if (!user) {
			console.error('saveUser: User è null o undefined');
			return;
		}
		localStorage.setItem(USER_KEY, JSON.stringify(user));
		console.log('Dati utente salvati in localStorage');
	} catch (e) {
		console.error('Errore nel salvataggio dei dati utente:', e);
	}
}

/**
 * Recupera dati utente da localStorage
 * @returns {Object|null}
 */
export function getUser() {
	try {
		var userJson = localStorage.getItem(USER_KEY);
		if (userJson) {
			return JSON.parse(userJson);
		}
	} catch (e) {
		console.error('Errore nel recupero dei dati utente:', e);
	}
	return null;
}

/**
 * Verifica se c'è un token valido in localStorage
 * @returns {boolean}
 */
export function isAuthenticated() {
	const token = getToken();
	return token !== null && isTokenValid(token);
}

/**
 * Verifica validità token JWT lato client (controlla scadenza)
 * @param {string} token
 * @returns {boolean}
 */
export function isTokenValid(token) {
	try {
		// Decodifica payload JWT (seconda parte del token)
		const parts = token.split('.');
		if (parts.length < 2) {
			return false;
		}

		const payload = JSON.parse(atob(parts[1]));
		
		// Controlla scadenza
		if (payload.exp && payload.exp < Math.floor(Date.now() / 1000)) {
			// Token scaduto: rimuovi da localStorage
			removeToken();
			return false;
		}

		return true;
	} catch (e) {
		console.error('Errore validazione token:', e);
		return false;
	}
}

/**
 * Verifica validità token lato server
 * @returns {Promise}
 */
export function verifyToken() {
	const token = getToken();
	console.log('verifyToken chiamato, token:', token ? token.substring(0, 20) + '...' : 'null');
	if (!token) {
		console.error('verifyToken: Nessun token presente');
		return Promise.reject(new Error('Nessun token presente'));
	}

	console.log('verifyToken: Chiamata a /api/auth/me con header Authorization:', 'Bearer ' + token.substring(0, 20) + '...');
	return axios.get(`${config.apiUrl}/api/auth/me`, {
		headers: {
			'Authorization': 'Bearer ' + token
		}
	}).then(function(response) {
		if (response.data && response.data.success && response.data.user) {
			console.log('api/auth/me - Utente ID:', response.data.user.id, '- Nickname:', response.data.user.nickname);
		}
		return response;
	});
}

/**
 * Estrae parametri da URL query string
 * @returns {Object|null} {token, email} o null
 */
export function getUrlParams() {
	const urlParams = new URLSearchParams(window.location.search);
	const token = urlParams.get('token');
	const email = urlParams.get('email');

	if (token && email) {
		return { token, email };
	}

	return null;
}

/**
 * Pulisce URL rimuovendo query parameters
 */
export function cleanUrl() {
	if (window.history && window.history.replaceState) {
		window.history.replaceState({}, document.title, window.location.pathname);
	}
}

/**
 * Determina il tipo di autenticazione dal token JWT
 * @param {string} token - Token JWT
 * @returns {string} 'facebook' se il token contiene player_id, 'email' se contiene user_id
 */
export function getAuthType(token) {
	try {
		// Decodifica payload JWT (seconda parte del token)
		const parts = token.split('.');
		if (parts.length < 2) {
			return 'email'; // Default a email se non può decodificare
		}

		const payload = JSON.parse(atob(parts[1]));
		
		// Se il token contiene player_id, è autenticazione Facebook
		if (payload.player_id) {
			return 'facebook';
		}
		
		// Se contiene user_id, è autenticazione email (magic link)
		if (payload.user_id) {
			return 'email';
		}
		
		// Default a email se non può determinare
		return 'email';
	} catch (e) {
		console.error('Errore determinazione tipo autenticazione:', e);
		return 'email'; // Default a email in caso di errore
	}
}

/**
 * Salva game_id selezionato in localStorage
 * @param {number} gameId - ID del gioco selezionato
 */
export function saveGameId(gameId) {
	try {
		if (!gameId) {
			console.error('saveGameId: gameId è null o undefined');
			return;
		}
		localStorage.setItem(GAME_ID_KEY, String(gameId));
		console.log('Game ID salvato in localStorage:', gameId);
	} catch (e) {
		console.error('Errore nel salvataggio del game_id:', e);
	}
}

/**
 * Recupera game_id selezionato da localStorage
 * @returns {number|null} ID del gioco o null se non presente
 */
export function getGameId() {
	try {
		var gameIdStr = localStorage.getItem(GAME_ID_KEY);
		if (gameIdStr) {
			var gameId = parseInt(gameIdStr, 10);
			if (!isNaN(gameId)) {
				return gameId;
			}
		}
	} catch (e) {
		console.error('Errore nel recupero del game_id:', e);
	}
	return null;
}

/**
 * Verifica se c'è un game_id salvato nel localStorage
 * @returns {boolean}
 */
export function hasGameId() {
	return getGameId() !== null;
}

/**
 * Rimuove game_id da localStorage
 */
export function removeGameId() {
	localStorage.removeItem(GAME_ID_KEY);
	localStorage.removeItem(GAME_MODE_ID_KEY);
	console.log('Game ID rimosso da localStorage');
}

/**
 * Salva game_mode_id selezionato in localStorage
 * @param {number} gameModeId - ID del game mode selezionato
 */
export function saveGameModeId(gameModeId) {
	try {
		if (!gameModeId) {
			console.error('saveGameModeId: gameModeId è null o undefined');
			return;
		}
		localStorage.setItem(GAME_MODE_ID_KEY, String(gameModeId));
		console.log('Game Mode ID salvato in localStorage:', gameModeId);
	} catch (e) {
		console.error('Errore nel salvataggio del game_mode_id:', e);
	}
}

/**
 * Recupera game_mode_id selezionato da localStorage
 * @returns {number|null} ID del game mode o null se non presente
 */
export function getGameModeId() {
	try {
		var gameModeIdStr = localStorage.getItem(GAME_MODE_ID_KEY);
		if (gameModeIdStr) {
			var gameModeId = parseInt(gameModeIdStr, 10);
			if (!isNaN(gameModeId)) {
				return gameModeId;
			}
		}
	} catch (e) {
		console.error('Errore nel recupero del game_mode_id:', e);
	}
	return null;
}
